import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, Loader2, Plus, Pencil, Trash2, X, Share2, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SOORT_META = {
  nieuw: { label: "Nieuw", bg: "#FFD700", fg: "#000000" },
  verbeterd: { label: "Verbeterd", bg: "#7CDBFF", fg: "#001018" },
  opgelost: { label: "Opgelost", bg: "#32D74B", fg: "#00150A" },
};

export function changelogSeenKey(userId) {
  return `tw1_changelog_seen_${userId || "anon"}`;
}

function newestStamp(entries) {
  // ISO strings sort lexicographically; created_at breaks datum ties.
  return entries.reduce((max, e) => {
    const s = `${e.datum}|${e.created_at || ""}`;
    return s > max ? s : max;
  }, "");
}

function formatDatum(datum) {
  try {
    const d = new Date(`${datum}T00:00:00`);
    return d.toLocaleDateString("nl-NL", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return datum;
  }
}

const EMPTY_FORM = { datum: "", titel: "", beschrijving: "", soort: "nieuw" };

export default function WhatsNewPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/changelog");
      setEntries(data);
      // Mark as seen: newest stamp -> localStorage, then tell the nav to clear its dot.
      try {
        localStorage.setItem(changelogSeenKey(user?.id), newestStamp(data));
        window.dispatchEvent(new Event("tw1:changelog-seen"));
      } catch { /* fail-soft */ }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const e of entries) {
      if (!map.has(e.datum)) map.set(e.datum, []);
      map.get(e.datum).push(e);
    }
    return Array.from(map.entries()).sort((a, b) => (a[0] < b[0] ? 1 : -1));
  }, [entries]);

  const openNew = () => {
    setEditId(null);
    setForm({ ...EMPTY_FORM, datum: new Date().toISOString().slice(0, 10) });
    setFormOpen(true);
  };
  const openEdit = (e) => {
    setEditId(e.id);
    setForm({ datum: e.datum, titel: e.titel, beschrijving: e.beschrijving, soort: e.soort });
    setFormOpen(true);
  };
  const closeForm = () => { setFormOpen(false); setEditId(null); setForm(EMPTY_FORM); };

  const submit = async (ev) => {
    ev.preventDefault();
    if (saving) return;
    if (!form.datum || !form.titel.trim() || !form.beschrijving.trim()) {
      toast.error("Vul datum, titel en beschrijving in.");
      return;
    }
    setSaving(true);
    try {
      if (editId) {
        await api.patch(`/changelog/${editId}`, form);
        toast.success("Wijziging bijgewerkt.");
      } else {
        await api.post("/changelog", form);
        toast.success("Wijziging toegevoegd.");
      }
      closeForm();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Deze wijziging permanent verwijderen?")) return;
    try {
      await api.delete(`/changelog/${id}`);
      toast.success("Wijziging verwijderd.");
      setEntries((prev) => prev.filter((e) => e.id !== id));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const shareEntry = async (e) => {
    const text = `${e.titel}\n\n${e.beschrijving}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: e.titel, text });
        return;
      } catch (err) {
        if (err?.name === "AbortError") return; // user cancelled the share sheet — do nothing
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Gekopieerd.");
    } catch {
      toast.error("Kopiëren mislukt.");
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-8 py-8 sm:py-12" data-testid="whatsnew-page">
      <div className="flex items-start justify-between gap-3 mb-1">
        <div className="flex items-center gap-2.5">
          <Sparkles size={18} className="text-tw-yellow" strokeWidth={2.5} />
          <h1 className="font-display text-2xl sm:text-3xl font-black text-white tracking-tight">Wat is er nieuw</h1>
        </div>
        {isAdmin && !formOpen && (
          <button type="button" onClick={openNew} data-testid="changelog-add-btn"
            className="tw-press inline-flex items-center gap-1.5 px-3 py-2 border border-tw text-tw-muted hover:text-tw-yellow hover:border-tw-yellow uppercase tracking-[0.2em] text-[10px] font-bold">
            <Plus size={13} strokeWidth={2.8} /> Wijziging
          </button>
        )}
      </div>
      <p className="text-tw-muted text-sm mb-6">De laatste verbeteringen aan Vocal — het brein groeit mee.</p>

      {isAdmin && formOpen && (
        <form onSubmit={submit} data-testid="changelog-form"
          className="mb-8 border border-tw-yellow/30 bg-tw-yellow/5 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-mono text-[10px] tracking-[0.3em] text-tw-yellow uppercase">
              {editId ? "Wijziging bewerken" : "Nieuwe wijziging"}
            </div>
            <button type="button" onClick={closeForm} data-testid="changelog-form-close" className="tw-press text-tw-muted hover:text-white">
              <X size={16} strokeWidth={2.5} />
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-3 mb-3">
            <Input type="date" value={form.datum} data-testid="changelog-form-datum"
              onChange={(e) => setForm((f) => ({ ...f, datum: e.target.value }))}
              className="bg-tw-black border-tw text-white h-11 rounded-none focus:border-tw-yellow" />
            <Select value={form.soort} onValueChange={(v) => setForm((f) => ({ ...f, soort: v }))}>
              <SelectTrigger data-testid="changelog-form-soort" className="bg-tw-black border-tw text-white h-11 rounded-none focus:border-tw-yellow">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-tw-surface border-tw text-white rounded-none">
                {Object.entries(SOORT_META).map(([k, m]) => (
                  <SelectItem key={k} value={k} className="rounded-none">{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Input value={form.titel} maxLength={100} data-testid="changelog-form-titel"
            onChange={(e) => setForm((f) => ({ ...f, titel: e.target.value }))}
            placeholder="Titel (kort en duidelijk)"
            className="mb-3 bg-tw-black border-tw text-white h-11 rounded-none focus:border-tw-yellow" />
          <Textarea value={form.beschrijving} maxLength={500} data-testid="changelog-form-beschrijving"
            onChange={(e) => setForm((f) => ({ ...f, beschrijving: e.target.value }))}
            placeholder="Wat betekent dit voor de gebruiker? Schrijf in gewone taal."
            className="mb-3 bg-tw-black border-tw text-white rounded-none focus:border-tw-yellow min-h-[80px]" />
          <button type="submit" disabled={saving} data-testid="changelog-form-submit"
            className="tw-press px-4 h-11 bg-tw-yellow text-black font-bold uppercase tracking-[0.15em] text-[11px] disabled:opacity-40">
            {saving ? <Loader2 size={14} className="animate-spin" /> : (editId ? "Opslaan" : "Toevoegen")}
          </button>
        </form>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-tw-muted py-16 justify-center" data-testid="changelog-loading">
          <Loader2 className="animate-spin" size={18} /> <span className="font-mono text-sm">Laden…</span>
        </div>
      ) : entries.length === 0 ? (
        <div className="text-center py-16" data-testid="changelog-empty">
          <Sparkles size={32} className="text-tw-muted mx-auto mb-4" strokeWidth={1.5} />
          <p className="text-white font-medium">Nog geen wijzigingen</p>
          <p className="text-tw-muted text-sm mt-2">Zodra er iets verandert, zie je het hier terug.</p>
        </div>
      ) : (
        <div className="space-y-8" data-testid="changelog-list">
          {grouped.map(([datum, list]) => (
            <div key={datum} className="tw-fadeup" data-testid={`changelog-group-${datum}`}>
              <div className="font-mono text-[10px] tracking-[0.3em] text-tw-muted uppercase mb-3 border-b border-tw pb-2">
                {formatDatum(datum)}
              </div>
              <ul className="space-y-4">
                {list.map((e) => {
                  const meta = SOORT_META[e.soort] || SOORT_META.nieuw;
                  return (
                    <li key={e.id} data-testid={`changelog-entry-${e.id}`} className="flex gap-3">
                      <span data-testid={`changelog-chip-${e.soort}`}
                        className="shrink-0 self-start mt-1 px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-[0.15em] leading-none whitespace-nowrap"
                        style={{ backgroundColor: meta.bg, color: meta.fg }}>
                        {meta.label}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-start justify-between gap-2">
                          <h2 className="font-display text-base sm:text-lg font-bold text-white leading-snug tracking-tight">{e.titel}</h2>
                          <div className="flex items-center gap-1 shrink-0">
                            <button type="button" onClick={() => shareEntry(e)} data-testid={`changelog-share-${e.id}`}
                              className="tw-press p-1 text-tw-muted hover:text-tw-yellow" title="Delen / kopiëren">
                              {typeof navigator !== "undefined" && navigator.share ? <Share2 size={14} strokeWidth={2.4} /> : <Copy size={14} strokeWidth={2.4} />}
                            </button>
                            {isAdmin && (
                              <>
                                <button type="button" onClick={() => openEdit(e)} data-testid={`changelog-edit-${e.id}`}
                                  className="tw-press p-1 text-tw-muted hover:text-tw-yellow" title="Bewerken">
                                  <Pencil size={14} strokeWidth={2.4} />
                                </button>
                                <button type="button" onClick={() => remove(e.id)} data-testid={`changelog-delete-${e.id}`}
                                  className="tw-press p-1 text-tw-muted hover:text-tw-red" title="Verwijderen">
                                  <Trash2 size={14} strokeWidth={2.4} />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                        <p className="text-tw-muted text-sm leading-relaxed mt-1">{e.beschrijving}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
